import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, Plus, Shield, Sparkles } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/features/")({
	component: FeaturesIndexPage,
});

const adminClient = createClient(AdminService, transport);

function FeaturesIndexPage() {
	const { t } = useTranslation("admin");
	useBreadcrumbs([
		{ label: t("admin_root"), href: "/admin" },
		{ label: t("features_title") },
	]);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [createOpen, setCreateOpen] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "features"],
		queryFn: () => adminClient.listFeatures({}),
	});

	const features = data?.features ?? [];

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<Skeleton className="h-8 w-48" />
					<Skeleton className="h-9 w-32" />
				</div>
				<Skeleton className="h-64" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-end">
				<Dialog open={createOpen} onOpenChange={setCreateOpen}>
					<DialogTrigger asChild>
						<Button>
							<Plus className="h-4 w-4 mr-1.5" />
							{t("features_create")}
						</Button>
					</DialogTrigger>
					<DialogContent>
						<CreateFeatureForm
							onSuccess={() => {
								setCreateOpen(false);
								queryClient.invalidateQueries({
									queryKey: ["admin", "features"],
								});
							}}
						/>
					</DialogContent>
				</Dialog>
			</div>

			{/* Plugin Governance */}
			<PluginGovernanceSection />

			{features.length === 0 ? (
				<div className="rounded-md border py-12 text-center text-muted-foreground">
					<Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
					<p>{t("features_empty_title")}</p>
					<p className="text-sm mt-1">{t("features_empty_description")}</p>
				</div>
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/30">
								<TableHead className="text-left font-medium px-4 py-2.5">
									{t("features_col_name")}
								</TableHead>
								<TableHead className="text-left font-medium px-4 py-2.5">
									{t("features_col_slug")}
								</TableHead>
								<TableHead className="text-left font-medium px-4 py-2.5">
									{t("features_col_description")}
								</TableHead>
								<TableHead className="text-center font-medium px-4 py-2.5">
									{t("features_col_plugins")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{features.map((feature) => (
								<TableRow
									key={feature.id}
									className="border-b last:border-b-0 hover:bg-muted/50 cursor-pointer transition-colors"
									onClick={() =>
										navigate({
											to: "/admin/features/$id",
											params: { id: feature.id },
										})
									}
								>
									<TableCell className="px-4 py-3 font-medium">{feature.name}</TableCell>
									<TableCell className="px-4 py-3">
										<code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
											{feature.slug}
										</code>
									</TableCell>
									<TableCell className="px-4 py-3 text-muted-foreground max-w-xs truncate">
										{feature.description || "—"}
									</TableCell>
									<TableCell className="px-4 py-3 text-center">
										<Badge variant="outline" className="text-xs">
											{feature.plugins.length}
										</Badge>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}

function PluginGovernanceSection() {
	const { t } = useTranslation("admin");
	const [requireApproval, setRequireApproval] = useState(false);

	return (
		<div className="rounded-md border p-4 space-y-4">
			<div className="flex items-center gap-2">
				<Shield className="h-4 w-4" />
				<h3 className="text-sm font-medium">
					{t("features_governance_title")}
				</h3>
			</div>

			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm">{t("features_governance_require_approval")}</p>
					<p className="text-xs text-muted-foreground">
						{t("features_governance_require_approval_hint")}
					</p>
				</div>
				<Switch
					checked={requireApproval}
					onCheckedChange={setRequireApproval}
				/>
			</div>

			<div>
				<h4 className="text-sm font-medium mb-2">
					{t("features_governance_log_title")}
				</h4>
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/30">
								<TableHead className="text-left font-medium px-4 py-2">
									{t("features_governance_log_col_plugin")}
								</TableHead>
								<TableHead className="text-left font-medium px-4 py-2">
									{t("features_governance_log_col_action")}
								</TableHead>
								<TableHead className="text-left font-medium px-4 py-2">
									{t("features_governance_log_col_user")}
								</TableHead>
								<TableHead className="text-left font-medium px-4 py-2">
									{t("features_governance_log_col_date")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow>
								<TableCell
									colSpan={4}
									className="px-4 py-6 text-center text-muted-foreground text-xs"
								>
									{t("features_governance_log_empty")}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</div>
			</div>
		</div>
	);
}

function CreateFeatureForm({ onSuccess }: { onSuccess: () => void }) {
	const { t } = useTranslation("admin");
	const [slug, setSlug] = useState("");
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [impact, setImpact] = useState("");
	const [icon, setIcon] = useState("");

	const mutation = useMutation({
		mutationFn: () =>
			adminClient.createFeature({
				slug,
				name,
				description,
				impact,
				icon,
			}),
		onSuccess,
	});

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t("features_create_title")}</DialogTitle>
				<DialogDescription>
					{t("features_create_description")}
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					<Label htmlFor="slug">{t("features_create_slug")}</Label>
					<Input
						id="slug"
						placeholder={t("features_create_slug_placeholder")}
						value={slug}
						onChange={(e) => setSlug(e.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="name">{t("features_create_name")}</Label>
					<Input
						id="name"
						placeholder={t("features_create_name_placeholder")}
						value={name}
						onChange={(e) => setName(e.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="description">
						{t("features_create_description_label")}
					</Label>
					<Textarea
						id="description"
						placeholder={t("features_create_description_placeholder")}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						rows={2}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="impact">{t("features_create_impact")}</Label>
					<Textarea
						id="impact"
						placeholder={t("features_create_impact_placeholder")}
						value={impact}
						onChange={(e) => setImpact(e.target.value)}
						rows={2}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="icon">{t("features_create_icon")}</Label>
					<Input
						id="icon"
						placeholder={t("features_create_icon_placeholder")}
						value={icon}
						onChange={(e) => setIcon(e.target.value)}
					/>
				</div>
			</div>
			<DialogFooter>
				<Button
					onClick={() => mutation.mutate()}
					disabled={mutation.isPending || !slug || !name}
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
							{t("features_creating")}
						</>
					) : (
						t("common:create")
					)}
				</Button>
			</DialogFooter>
		</>
	);
}
